import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
  Modal,
  Image,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import theme from '../../theme';
import { AssignmentHistory } from '../../types';
import { deleteAssignmentHistory } from '../../api/supabase';
import { useAssignmentStore } from '../../stores/assignmentStore';

const ManageAssignmentScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const assignment = route.params?.assignment;
  const [deleting, setDeleting] = useState(false);
  const [photoModalVisible, setPhotoModalVisible] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const { fetchAssignments } = useAssignmentStore();

  if (!assignment) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No assignment data available</Text>
      </View>
    );
  }

  // Check if assignment has photos
  const deliveryPhotos = assignment.delivery_photos || [];
  const receivingPhotos = assignment.receiving_photos || [];
  const allPhotos = [...deliveryPhotos, ...receivingPhotos];
  const hasPhotos = allPhotos.length > 0;

  const handleEdit = () => {
    console.log('✏️ Navigating to Edit Assignment');
    navigation.navigate('EditAssignment', { assignment });
  };

  const handleViewPhotos = () => {
    if (hasPhotos) {
      setSelectedPhotoIndex(0);
      setPhotoModalVisible(true);
    }
  };

  const handleNextPhoto = () => {
    setSelectedPhotoIndex((prev) => (prev + 1) % allPhotos.length);
  };

  const handlePreviousPhoto = () => {
    setSelectedPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };

  const handleDelete = async () => {
    console.log('🗑️ handleDelete called for assignment:', assignment.id);
    
    // Check user permission for DELETE_ASSIGNMENTS
    const { useAuthStore } = require('../../stores/authStore');
    const { PERMISSIONS } = require('../../constants/permissions');
    const hasDeletePermission = useAuthStore.getState().hasPermission(PERMISSIONS.DELETE_ASSIGNMENTS);
    
    if (!hasDeletePermission) {
      const permissionMessage = 'ليس لديك صلاحية لحذف التعيينات\nYou do not have permission to delete assignments';
      if (Platform.OS === 'web') {
        alert(permissionMessage);
      } else {
        Alert.alert('صلاحية مطلوبة / Permission Required', permissionMessage);
      }
      return;
    }
    
    const confirmMessage = `هل أنت متأكد من حذف هذا التعيين؟\nAre you sure you want to delete this assignment?\n\n${assignment.vehicle?.license_plate} - ${assignment.primaryDriver?.name}`;
    
    // Use window.confirm for web, Alert.alert for native
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) {
        console.log('❌ Delete cancelled by user');
        return;
      }
      
      // Proceed with deletion
      try {
        setDeleting(true);
        console.log('🗑️ Deleting assignment:', assignment.id);
        
        await deleteAssignmentHistory(assignment.id);
        
        console.log('✅ Assignment deleted successfully');
        
        // Refresh assignments first
        await fetchAssignments();
        
        // Navigate back immediately
        navigation.goBack();
        
        // Show success message after navigation
        setTimeout(() => {
          alert('نجح / Success\n\nتم حذف التعيين بنجاح\nAssignment deleted successfully');
        }, 300);
      } catch (error) {
        console.error('❌ Error deleting assignment:', error);
        alert('خطأ / Error\n\nفشل حذف التعيين\nFailed to delete assignment');
      } finally {
        setDeleting(false);
      }
    } else {
      // Native Alert.alert
      Alert.alert(
        'تأكيد الحذف / Confirm Delete',
        confirmMessage,
        [
          {
            text: 'إلغاء / Cancel',
            style: 'cancel',
          },
          {
            text: 'حذف / Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                setDeleting(true);
                console.log('🗑️ Deleting assignment:', assignment.id);
                
                await deleteAssignmentHistory(assignment.id);
                
                console.log('✅ Assignment deleted successfully');
                
                // Refresh assignments first
                await fetchAssignments();
                
                // Navigate back immediately
                navigation.goBack();
                
                // Show success message after navigation
                setTimeout(() => {
                  Alert.alert(
                    'نجح / Success',
                    'تم حذف التعيين بنجاح\nAssignment deleted successfully'
                  );
                }, 300);
              } catch (error) {
                console.error('❌ Error deleting assignment:', error);
                Alert.alert(
                  'خطأ / Error',
                  'فشل حذف التعيين\nFailed to delete assignment'
                );
              } finally {
                setDeleting(false);
              }
            },
          },
        ]
      );
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>إدارة التعيين / Manage Assignment</Text>
          <Text style={styles.headerSubtitle}>
            {assignment.vehicle?.license_plate} - {assignment.vehicle?.make_model}
          </Text>
        </View>
      </View>

      <ScrollView style={styles.content}>
        {/* Assignment Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.sectionTitle}>معلومات التعيين / Assignment Info</Text>
          
          <View style={styles.infoRow}>
            <Ionicons name="car" size={20} color={theme.colors.primary} />
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>المركبة / Vehicle</Text>
              <Text style={styles.infoValue}>
                {assignment.vehicle?.license_plate} - {assignment.vehicle?.make_model}
              </Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <Ionicons name="person" size={20} color={theme.colors.primary} />
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>السائق الأساسي / Primary Driver</Text>
              <Text style={styles.infoValue}>{assignment.primaryDriver?.name}</Text>
            </View>
          </View>

          {assignment.secondaryDriver && (
            <View style={styles.infoRow}>
              <Ionicons name="people" size={20} color={theme.colors.success} />
              <View style={styles.infoContent}>
                <Text style={styles.infoLabel}>السائق الثانوي / Secondary Driver</Text>
                <Text style={styles.infoValue}>{assignment.secondaryDriver.name}</Text>
              </View>
            </View>
          )}

          <View style={styles.infoRow}>
            <Ionicons name="calendar" size={20} color={theme.colors.primary} />
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>تاريخ التعيين / Assignment Date</Text>
              <Text style={styles.infoValue}>
                {new Date(assignment.assignment_date).toLocaleDateString()}
              </Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <Ionicons name="cash" size={20} color={theme.colors.primary} />
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>الحصة / Share</Text>
              <Text style={styles.infoValue}>
                {assignment.monthly_rent_rate 
                  ? `AED ${Number(assignment.monthly_rent_rate).toFixed(2)}/mo${assignment.daily_rate ? ` (AED ${Number(assignment.daily_rate).toFixed(2)}/day)` : ''}`
                  : assignment.daily_rate
                  ? `AED ${Number(assignment.daily_rate).toFixed(2)}/day`
                  : 'Not set'
                }
              </Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <Ionicons 
              name={assignment.status === 'active' ? 'checkmark-circle' : 'close-circle'} 
              size={20} 
              color={assignment.status === 'active' ? theme.colors.success : theme.colors.error} 
            />
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>الحالة / Status</Text>
              <Text style={[styles.infoValue, { color: getStatusColor(assignment.status) }]}>
                {assignment.status.toUpperCase()}
              </Text>
            </View>
          </View>
        </View>

        {/* Action Options */}
        <View style={styles.actionsCard}>
          <Text style={styles.sectionTitle}>الإجراءات / Actions</Text>

          {/* Edit Option */}
          <TouchableOpacity 
            style={styles.actionOption}
            onPress={handleEdit}
            activeOpacity={0.7}
          >
            <View style={[styles.actionIconContainer, { backgroundColor: theme.colors.primary + '20' }]}>
              <Ionicons name="create-outline" size={28} color={theme.colors.primary} />
            </View>
            <View style={styles.actionContent}>
              <Text style={styles.actionTitle}>تعديل التعيين / Edit Assignment</Text>
              <Text style={styles.actionDescription}>
                تعديل التفاصيل والأسعار والتواريخ
                {'\n'}
                Modify details, rates, and dates
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={24} color={theme.colors.gray400} />
          </TouchableOpacity>

          {/* View Attachments Option - Always show with status */}
          <TouchableOpacity 
            style={[styles.actionOption, !hasPhotos && { opacity: 0.6 }]}
            onPress={hasPhotos ? handleViewPhotos : undefined}
            activeOpacity={hasPhotos ? 0.7 : 1}
            disabled={!hasPhotos}
          >
            <View style={[styles.actionIconContainer, { backgroundColor: hasPhotos ? theme.colors.info + '20' : theme.colors.gray200 }]}>
              <Ionicons name="images-outline" size={28} color={hasPhotos ? theme.colors.info : theme.colors.gray400} />
            </View>
            <View style={styles.actionContent}>
              <Text style={[styles.actionTitle, !hasPhotos && { color: theme.colors.gray500 }]}>
                {hasPhotos 
                  ? `عرض المرفقات (${allPhotos.length}) / View Attachments (${allPhotos.length})`
                  : 'لا توجد مرفقات / No Attachments'
                }
              </Text>
              <Text style={styles.actionDescription}>
                {hasPhotos ? (
                  <>
                    {deliveryPhotos.length > 0 && `تسليم: ${deliveryPhotos.length} / Delivery: ${deliveryPhotos.length}`}
                    {deliveryPhotos.length > 0 && receivingPhotos.length > 0 && ' • '}
                    {receivingPhotos.length > 0 && `استلام: ${receivingPhotos.length} / Receiving: ${receivingPhotos.length}`}
                  </>
                ) : (
                  'لم يتم رفع صور / No photos uploaded'
                )}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={24} color={hasPhotos ? theme.colors.gray400 : theme.colors.gray300} />
          </TouchableOpacity>

          {/* Delete Option */}
          <TouchableOpacity 
            style={[styles.actionOption, styles.destructiveAction]}
            onPress={handleDelete}
            disabled={deleting}
            activeOpacity={0.7}
          >
            <View style={[styles.actionIconContainer, { backgroundColor: theme.colors.error + '20' }]}>
              <Ionicons name="trash-outline" size={28} color={theme.colors.error} />
            </View>
            <View style={styles.actionContent}>
              <Text style={[styles.actionTitle, styles.destructiveText]}>
                حذف التعيين / Delete Assignment
              </Text>
              <Text style={styles.actionDescription}>
                حذف هذا التعيين نهائياً
                {'\n'}
                Permanently delete this assignment
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={24} color={theme.colors.error} />
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Photo Viewer Modal */}
      <Modal
        visible={photoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setPhotoModalVisible(false)}
      >
        <View style={styles.photoModalOverlay}>
          <View style={styles.photoModalHeader}>
            <Text style={styles.photoModalTitle}>
              صورة {selectedPhotoIndex + 1} من {allPhotos.length} / Photo {selectedPhotoIndex + 1} of {allPhotos.length}
            </Text>
            <TouchableOpacity onPress={() => setPhotoModalVisible(false)}>
              <Ionicons name="close" size={28} color={theme.colors.white} />
            </TouchableOpacity>
          </View>

          <View style={styles.photoContainer}>
            {allPhotos.length > 0 && (
              <Image
                source={{ uri: allPhotos[selectedPhotoIndex] }}
                style={styles.photoImage}
                resizeMode="contain"
              />
            )}
          </View>

          {allPhotos.length > 1 && (
            <View style={styles.photoControls}>
              <TouchableOpacity 
                style={styles.photoControlButton}
                onPress={handlePreviousPhoto}
              >
                <Ionicons name="chevron-back" size={32} color={theme.colors.white} />
              </TouchableOpacity>

              <View style={styles.photoIndicators}>
                {allPhotos.map((_, index) => (
                  <View
                    key={index}
                    style={[
                      styles.photoIndicator,
                      index === selectedPhotoIndex && styles.photoIndicatorActive
                    ]}
                  />
                ))}
              </View>

              <TouchableOpacity 
                style={styles.photoControlButton}
                onPress={handleNextPhoto}
              >
                <Ionicons name="chevron-forward" size={32} color={theme.colors.white} />
              </TouchableOpacity>
            </View>
          )}

          <Text style={styles.photoTypeLabel}>
            {selectedPhotoIndex < deliveryPhotos.length 
              ? 'صورة التسليم / Delivery Photo' 
              : 'صورة الاستلام / Receiving Photo'}
          </Text>
        </View>
      </Modal>
    </View>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return theme.colors.success;
    case 'completed': return theme.colors.textSecondary;
    case 'cancelled': return theme.colors.error;
    default: return theme.colors.textSecondary;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingTop: 50,
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.sm,
    marginRight: theme.spacing.sm,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  summaryCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  infoContent: {
    flex: 1,
    marginLeft: theme.spacing.md,
  },
  infoLabel: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  actionsCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  actionOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.gray50,
    marginBottom: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  destructiveAction: {
    backgroundColor: theme.colors.error + '10',
    borderColor: theme.colors.error + '30',
  },
  actionIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionContent: {
    flex: 1,
    marginLeft: theme.spacing.md,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  actionDescription: {
    fontSize: 12,
    color: theme.colors.gray600,
    lineHeight: 16,
  },
  destructiveText: {
    color: theme.colors.error,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
    marginTop: 50,
  },
  // Photo Modal Styles
  photoModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
  },
  photoModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingTop: 50,
    paddingBottom: theme.spacing.md,
  },
  photoModalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.white,
  },
  photoContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  photoImage: {
    width: Dimensions.get('window').width - 40,
    height: Dimensions.get('window').height - 300,
  },
  photoControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
  },
  photoControlButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoIndicators: {
    flexDirection: 'row',
    gap: 8,
  },
  photoIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  photoIndicatorActive: {
    backgroundColor: theme.colors.white,
    width: 24,
  },
  photoTypeLabel: {
    textAlign: 'center',
    fontSize: 14,
    color: theme.colors.white,
    paddingBottom: 30,
  },
});

export default ManageAssignmentScreen;
